//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as pinterestAnalyticsApi from '@apis/pinterest-analytics/pinterestAnalyticsApi';
import { IMlsMaterializedPinWithMetrics } from '@apis/pinterest-analytics/pinterestAnalyticsApiModel';
import * as universalScheduledPostsApi from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { IScheduledPin } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { isScheduledPin } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiUtils';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import { getAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import PostableStatus from '@models/PostableStatus';
import { EngagementMetric } from '@pinterest/pinterestConstants';
import { IPinterestSummaryPageState } from '@pinterest/redux/summary/PinterestSummaryModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IPinterestSummaryPageState = {
  upcomingPins: undefined,
  topRecentPins: [],
  flags: {
    isFetchingUpcomingPins: false,
    isFetchingTopRecentPins: false,
  },
  errors: {
    fetchUpcomingPinsError: undefined,
    fetchTopRecentPinsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestSummary,
  initialState,
  reducers: {
    requestUpcomingPins(state) {
      state.flags.isFetchingUpcomingPins = true;
      state.errors.fetchUpcomingPinsError = undefined;
    },
    receiveUpcomingPinsSucceeded(
      state,
      action: PayloadAction<IScheduledPin[]>,
    ) {
      state.flags.isFetchingUpcomingPins = false;
      state.upcomingPins = [...action.payload];
    },
    receiveUpcomingPinsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingUpcomingPins = false;
      state.errors.fetchUpcomingPinsError = action.payload;
    },
    requestTopRecentPins(state) {
      state.flags.isFetchingTopRecentPins = true;
      state.errors.fetchTopRecentPinsError = undefined;
    },
    receiveTopRecentPinsSucceeded(
      state,
      action: PayloadAction<IMlsMaterializedPinWithMetrics[]>,
    ) {
      state.topRecentPins = action.payload;
      state.flags.isFetchingTopRecentPins = false;
    },
    receiveTopRecentPinsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingTopRecentPins = false;
      state.errors.fetchTopRecentPinsError = action.payload;
    },
  },
});

export const getUpcomingScheduledPins = createThunk(
  () => async (dispatch, getState) => {
    const {
      flags: { isFetchingUpcomingPins },
    } = getState().pinterest.summary;

    if (isFetchingUpcomingPins) {
      return;
    }

    dispatch(actions.requestUpcomingPins());

    try {
      const { data: scheduledPosts } =
        await universalScheduledPostsApi.searchScheduledPosts({
          groups: [UniversalPostingGroupName.PinterestOrganicPinGroup],
          status: [PostableStatus.Scheduled, PostableStatus.Rescheduled],
          after: new Date(),
          limit: 4,
        });

      const scheduledPins = scheduledPosts.filter(isScheduledPin);

      dispatch(actions.receiveUpcomingPinsSucceeded(scheduledPins));
    } catch (e) {
      dispatch(actions.receiveUpcomingPinsFailed(getRequestErrorMessage(e)));
    }
  },
);

export const fetchRecentPins = createThunk(() => async (dispatch, getState) => {
  const {
    flags: { isFetchingTopRecentPins },
  } = getState().pinterest.summary;

  if (isFetchingTopRecentPins) {
    return;
  }

  dispatch(actions.requestTopRecentPins());

  try {
    const userTimeZone = getUserTimeZone(getState());
    const dateRange = getAliasedDateRange('last30Days', userTimeZone);

    const topRecentPins = await pinterestAnalyticsApi.getTopPins({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortByMetric: EngagementMetric.Impressions,
      createdInLast30DaysFilter: true,
    });

    dispatch(actions.receiveTopRecentPinsSucceeded(topRecentPins));
  } catch (e) {
    dispatch(actions.receiveTopRecentPinsFailed(getRequestErrorMessage(e)));
  }
});

export default reducer;
